import { useEffect, useState } from "react";
import { useBranding } from "../branding";

interface Component {
  name: string;
  ok: boolean;
}

interface StatusData {
  time: string;
  uptimeSec: number;
  maintenanceActive: boolean;
  maintenanceMessage: string;
  maintenanceUntil: string | null;
  components: Component[];
}

function fmtUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0 || d > 0) parts.push(`${h}h`);
  parts.push(`${m}min`);
  return parts.join(" ");
}

/** Página pública de status (/status) - sem login, pra consultar saúde do sistema e avisos de manutenção. */
export function StatusPage() {
  const { appName, logoDataUrl } = useBranding();
  const [data, setData] = useState<StatusData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    fetch("/api/status")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((j: StatusData) => setData(j))
      .catch(() => setError("Não foi possível carregar o status agora."));
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, []);

  const allOk = data?.components.every((c) => c.ok) ?? true;

  return (
    <div className="shared-page">
      <header className="shared-page-header">
        {logoDataUrl ? <img src={logoDataUrl} alt={appName} className="brand-logo" /> : <span className="brand-dot" />}
        <span>{appName} - Status</span>
      </header>
      <div className="shared-page-body">
        {error ? (
          <p className="shared-page-status shared-page-error">{error}</p>
        ) : !data ? (
          <p className="shared-page-status">Carregando…</p>
        ) : (
          <>
            {data.maintenanceActive && (
              <div className="status-maintenance-banner">
                <strong>Manutenção em andamento.</strong>{" "}
                {data.maintenanceMessage || "Algumas funções podem ficar indisponíveis temporariamente."}
                {data.maintenanceUntil && (
                  <> Previsão de término: {new Date(data.maintenanceUntil).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}.</>
                )}
              </div>
            )}
            <div className={`status-overall ${allOk ? "ok" : "degraded"}`}>
              <span className="status-overall-dot" aria-hidden />
              {allOk ? "Todos os sistemas operacionais" : "Alguns componentes com problema"}
            </div>
            <div className="status-components">
              {data.components.map((c) => (
                <div key={c.name} className="status-component-row">
                  <span className={`status-dot ${c.ok ? "ok" : "down"}`} aria-hidden />
                  <span className="status-component-name">{c.name}</span>
                  <span className={`status-component-state ${c.ok ? "ok" : "down"}`}>
                    {c.ok ? "operacional" : "indisponível"}
                  </span>
                </div>
              ))}
            </div>
            <p className="shared-page-meta">
              Uptime do servidor: {fmtUptime(data.uptimeSec)} - atualizado às{" "}
              {new Date(data.time).toLocaleTimeString("pt-BR", { timeZone: "America/Sao_Paulo" })}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
